import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import authService from "../../services/api/authService";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import { useCallback } from "react";
import { QUERY_CONFIGS } from "../../lib/react-query/config";
import useAuthStore from "../../stores/authStore";
import profileService from "../../services/api/profileService";

export const authQueryKeys = {
  all: ["auth"],
  login: () => [...authQueryKeys.all, "login"],
  mfa: () => [...authQueryKeys.all, "mfa"],
  resendMfa: () => [...authQueryKeys.all, "resendMfa"],
  requestPasswordReset: () => [...authQueryKeys.all, "requestPasswordReset"],
  requestReactivation: () => [...authQueryKeys.all, "requestReactivation"],
  resetPassword: () => [...authQueryKeys.all, "resetPassword"],
  activateAccount: () => [...authQueryKeys.all, "activateAccount"],
  status: () => [...authQueryKeys.all, "status"],
  currentUser: () => [...authQueryKeys.all, "currentUser"],
  tokenValid: (token) => [...authQueryKeys.all, "tokenValid", token],
};

export const useLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials) =>
      authService
        .login(credentials.email, credentials.password)
        .then((response) => response.data),
    mutationKey: authQueryKeys.login(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useVerifyMfaMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token) =>
      authService.verifyMfaCode(token).then((response) => response),
    mutationKey: authQueryKeys.mfa(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useResendMfaCodeMutation = () => {
  const request = useCallback(async (email) => {
    try {
      const response = await authService.resendMfaCode(email);
      return response.data;
    } catch (error) {
      const errorMessage = extractAxiosErrorMessage(error, "Resend OTP failed");
      throw new Error(errorMessage);
    }
  }, []);

  return useMutation({
    mutationFn: request,
    mutationKey: authQueryKeys.resendMfa(),
    retry: false,
  });
};

export const useRequestPasswordReset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email) =>
      authService.requestPasswordReset(email).then((response) => response.data),
    mutationKey: authQueryKeys.requestPasswordReset(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useResetPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ token, password }) =>
      authService
        .resetPassword(token, password)
        .then((response) => response.data),
    mutationKey: authQueryKeys.resetPassword(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useActivateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ token, password }) =>
      authService
        .activateAccount(token, password)
        .then((response) => response.data),
    mutationKey: authQueryKeys.activateAccount(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useRequestReactivation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email) =>
      authService.requestReactivation(email).then((response) => response.data),
    mutationKey: authQueryKeys.requestReactivation(),
    onSuccess: () => {
      queryClient.invalidateQueries(authQueryKeys.status());
    },
    retry: false,
  });
};

export const useCheckActivationTokenQuery = (token) => {
  return useQuery({
    queryKey: authQueryKeys.tokenValid(token),
    queryFn: () =>
      authService.checkActivationTokenValid(token).then((res) => res.data),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const useCurrentUserQuery = (options = {}) => {
  const setComputedUser = useAuthStore((state) => state.setComputedUser);

  const request = async () => {
    const response = await profileService.getCurrentUser();
    if (response && typeof setComputedUser === "function") {
      setComputedUser(response);
    }
    return response;
  };

  return useQuery({
    queryKey: authQueryKeys.currentUser(),
    queryFn: request,
    ...QUERY_CONFIGS.CRITICAL,
    throwOnError: false,
    ...options,
  });
};

export default useCurrentUserQuery;
